import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { OverlayEventDetail } from '@ionic/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  PlatformEnum,
  PlatformService,
  SelectedCustomerState,
} from '@panamax/app-state';
import {
  CreditStatus,
  LastPaymentMethod,
} from '@payments/constants/payment-dashboard';
import {
  ArStatementFilterIds,
  ArStatementSortFields,
} from '@payments/pages/ar-statements/services/ar-statements.service';
import {
  PaymentFilter,
  PaymentFilterIds,
} from '@payments/pages/payment-activity/services/payment-activity.service';
import {
  DashboardFilter,
  DashboardFilterIds,
  DashboardSortFields,
} from '@payments/pages/payment-dashboard/services/payment-dashboard.service';
import {
  PaymentPreferenceFilter,
  PaymentPreferenceFilterIds,
  PaymentPreferenceSortFields,
} from '@payments/pages/payment-preferences/services/payment-preferences.service';
import { getSelectedFilterCustomers } from '@payments/selectors/payments.selector';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { CustomerSelectionPaymentsFilterMobileComponent } from '@shared/components/header/customer-selection/customer-selection-payments-filter-mobile/customer-selection-payments-filter-mobile.component';
import { CustomerSelectionPaymentsTouchComponent } from '@shared/components/header/customer-selection/customer-selection-payments-touch/customer-selection-payments-touch.component';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import {
  ModalFilter,
  ModalFilterCalendarData,
  ModalFilterCustomerData,
  ModalFilterListData,
  ModalFilterTextData,
  ModalSort,
  isFilterCalendarData,
  isFilterCustomerData,
  isFilterListData,
  isFilterTextEntryData,
} from '@shared/components/usf-filtering-modal/model/modal-filter';
import { UsfFilteringModalDesktopComponent } from '@shared/components/usf-filtering-modal/usf-filtering-modal-desktop/usf-filtering-modal-desktop.component';
import { UsfFilteringModalMobileComponent } from '@shared/components/usf-filtering-modal/usf-filtering-modal-mobile/usf-filtering-modal-mobile.component';
import { UsfFilteringDatesOptionComponent } from '@shared/components/usf-filtering-modal/usf-filtering-options/usf-filtering-dates-option/usf-filtering-dates-option.component';
import { UsfFilterListOptionsComponent } from '@shared/components/usf-filtering-modal/usf-filtering-options/usf-filtering-list-options/usf-filtering-list-options.component';
import { UsfFilterTextEntryOptionsComponent } from '@shared/components/usf-filtering-modal/usf-filtering-options/usf-filtering-text-entry-options/usf-filter-text-entry-options.component';
import { UsfSortOptionsComponent } from '@shared/components/usf-filtering-modal/usf-filtering-options/usf-sort-options/usf-sort-options.component';
import { ModalService } from '@shared/services/modal/modal.service';
import { Customer } from '@usf/customer-types';
import {
  Filter,
  InvoiceFilterIds,
  SortDirection,
  SortFilter,
  StatusSummaryState,
} from '@usf/ngrx-payment';
import {
  PaymentActivitySortFields,
  PaymentMethod,
  SortFields,
  Status,
  TransactionGroupFilter,
} from '@usf/payment-types';
import dayjs from 'dayjs';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { CustomerSelectionOption } from '../../customer/models/customer-selection-option.model';
import { CustomerSelectionVM } from '../../customer/models/customer-selection-vm.model';
import { PaymentsService } from './payments.service';

export interface ModalFilterService {
  getFiltersAndSortInformation$(): Observable<{
    filters: Filter[];
    sort: SortFilter[];
  }>;
  getStatusOptions(): Map<Status, string>;
  setFilter(invoiceFilters: AllFilter[], restrictToOpen?: boolean): void;
  setSort(sortOrder: SortFilter[]);
  clearFiltersAndResetSort();
}

export interface FilterBaseVM {
  filters: Filter[];
  sort: SortFilter[];
  filterDateLabel: string;
  filterCount: number;
  statusSummary?: StatusSummaryState;
  breadcrumbs?: Crumb[];
}

export interface PaymentModalOutput {
  filters: ModalFilter[];
  sort: SortFilter[];
}

export type AllFilter =
  | DashboardFilter
  | Filter
  | PaymentFilter
  | PaymentPreferenceFilter;

export type AllFilterIds =
  | ArStatementFilterIds
  | DashboardFilterIds
  | InvoiceFilterIds
  | PaymentFilterIds
  | PaymentPreferenceFilterIds;

export enum ModalType {
  arStatement,
  invoice,
  paymentActivity,
  paymentPreferences,
  sellerDashboard,
}

@Injectable({
  providedIn: 'root',
})
export class PaymentsModalService implements OnDestroy {
  customerSelectorLabel: string;
  filterSelection: Filter[];
  filterService: ModalFilterService;
  searchText$ = new BehaviorSubject<string>('');
  sortSelection: SortFilter[];
  filterAndSortSub$: Subscription;
  clearFiltersEvent$: EventEmitter<ModalFilter[]>;

  constructor(
    readonly platformService: PlatformService,
    private store: Store,
    private paymentsService: PaymentsService,
    private modalService: ModalService,
    private modalController: ModalController,
    private translateService: TranslateService,
  ) {}

  ngOnDestroy() {
    this.clearFiltersEvent$?.unsubscribe();
    this.filterAndSortSub$?.unsubscribe();
  }

  getFiltersSelectedCustomers$(): Observable<SelectedCustomerState[]> {
    return this.store.select(getSelectedFilterCustomers);
  }

  async openModal(
    filterService: ModalFilterService,
    selectedFilter?: AllFilterIds,
    showISRFilter: boolean = false,
    modalType: ModalType = ModalType.invoice,
    showOrderNumberFilter: boolean = false,
    hideUsersCustomers: boolean = false,
    additionalCustomers: Customer[] = [],
  ) {
    this.filterService = filterService;
    const isTouch = this.platformService.isTouch.value;
    const isMobile = this.platformService.platformType === PlatformEnum.mobile;

    this.filterAndSortSub$?.unsubscribe();
    this.filterAndSortSub$ = this.filterService
      .getFiltersAndSortInformation$()
      .subscribe(filtersAndSort => {
        this.filterSelection = filtersAndSort.filters;
        this.sortSelection = filtersAndSort.sort;
      });

    let filters: ModalFilter[];
    let sortOptions: ModalSort[];
    switch (modalType) {
      case ModalType.arStatement:
        filters = this.createFiltersForArStatements();
        sortOptions = this.createSortForArStatements();
        break;
      case ModalType.invoice:
        filters = this.createCommonFilters(
          showISRFilter,
          showOrderNumberFilter,
        );
        sortOptions = this.createSorts(showOrderNumberFilter);
        break;
      case ModalType.paymentActivity:
        filters = this.createFiltersForPaymentActivity();
        sortOptions = this.createSortsForPaymentActivity();
        break;
      case ModalType.paymentPreferences:
        filters = this.createFiltersForPaymentPreferences();
        sortOptions = this.createSortsForPaymentPreferences();
        break;
      case ModalType.sellerDashboard:
        filters = this.createFiltersForDashboard(
          hideUsersCustomers,
          additionalCustomers,
        );
        sortOptions = this.createSortsForDashboard();
        break;
    }

    const modalComponent = isMobile
      ? UsfFilteringModalMobileComponent
      : UsfFilteringModalDesktopComponent;

    this.clearFiltersEvent$?.unsubscribe();
    this.clearFiltersEvent$ = new EventEmitter();
    this.clearFiltersEvent$.subscribe(data => {
      this.clearFilters(data, hideUsersCustomers, additionalCustomers);
    });

    const sortComponent = {
      filterName: 'sortBy', // Not an official InvoiceFilterIds enum
      component: UsfSortOptionsComponent,
      data: {
        title: 'Sort By',
        sortOptions: sortOptions,
        sortSelection: this.sortSelection,
        platformType: this.platformService.platformType,
      },
    };

    const filterIndex = filters.findIndex(
      filter => filter.filterName === selectedFilter,
    );

    const modalData = {
      isTouch,
      filters,
      title: isTouch ? 'Sort & Filter' : 'Filter',
      selectedFilterIndex: ~filterIndex || isMobile ? filterIndex : 0,
      sortSelection: this.sortSelection,
      clearFiltersEvent: this.clearFiltersEvent$,
      sortComponent,
    };

    this.modalService.setModalOptions(
      isTouch,
      ModalWithNavComponent,
      {
        rootPage: modalComponent,
        rootPageParams: modalData,
      },
      'usf-filtering-modal',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    modal.onDidDismiss().then(event => {
      this.handleModalDismiss(event, filters, modalType);
    });

    return await modal.present();
  }

  public getCustomerSelectionVM$(
    showSelectAll?: boolean,
    customerNumbersToShow?: number[],
    hideUsersCustomers: boolean = false,
    additionalCustomers: Customer[] = [],
  ): Observable<CustomerSelectionVM> {
    return combineLatest([
      this.paymentsService.getPaymentsVM$(),
      this.getFiltersSelectedCustomers$(),
      this.searchText$,
    ]).pipe(
      map(([paymentsVM, selectedCustomers, searchText]) => {
        let displayedCustomers = customerNumbersToShow
          ? paymentsVM.customers.filter(customer =>
              customerNumbersToShow.includes(customer.customerNumber),
            )
          : paymentsVM.customers;

        if (hideUsersCustomers) {
          displayedCustomers = additionalCustomers;
        }

        displayedCustomers = displayedCustomers.concat(additionalCustomers);

        //remove duplicates
        displayedCustomers = displayedCustomers.filter(
          (obj1, index, arr) =>
            arr.findIndex(
              obj2 => obj2.customerNumber === obj1.customerNumber,
            ) === index,
        );

        return this.constructCustomerSelectionVM(
          displayedCustomers,
          selectedCustomers,
          showSelectAll ?? paymentsVM.showAllCustomersOption,
          searchText,
        );
      }),
    );
  }

  private clearFilters(
    filters: ModalFilter[],
    hideUsersCustomers: boolean,
    additionalCustomers: Customer[],
  ) {
    filters.forEach(filter => {
      if (isFilterCalendarData(filter.data)) {
        const calendarData = filter.data;
        calendarData.fromDate = 0;
        calendarData.toDate = 0;
        calendarData.subTitle$.next(this.formattedDateLabel(0, 0));
      } else if (isFilterCustomerData(filter.data)) {
        const customerData = filter.data as ModalFilterCustomerData;
        customerData.vm$ = this.getCustomerSelectionVM$(
          undefined,
          undefined,
          hideUsersCustomers,
          additionalCustomers,
        );
        customerData.selectedCustomers = [];
      } else if (isFilterListData(filter.data)) {
        const listData = filter.data as ModalFilterListData;
        listData.newSelectedData = [];
      } else if (isFilterTextEntryData(filter.data)) {
        const textData = filter.data as ModalFilterTextData;
        textData.searchText = '';
      }
    });
  }

  private constructCustomerSelectionVM(
    customers: Customer[],
    selectedCustomerState: SelectedCustomerState[],
    showAllCustomersOption: boolean,
    searchText: string,
  ): CustomerSelectionVM {
    const isDesktop =
      this.platformService.getPlatformType() === PlatformEnum.desktop;
    const customerRowHeight = isDesktop ? 54 : 58;
    const customerSelectionOptions: CustomerSelectionOption[] = [];
    const rowHeights: number[] = [];

    customers
      .filter(
        customer =>
          customer.customerName
            .toLowerCase()
            .includes(searchText.toLowerCase()) ||
          customer.customerNumber.toString().includes(searchText.toLowerCase()),
      )
      .forEach(customer => {
        const title =
          customer.customerName +
          (customer.customerNumber === 0
            ? ''
            : ` (${customer.customerNumber})`);
        const alphabets = title.match('[a-zA-Z]{1}');
        let customerInfo: CustomerSelectionOption = {
          title,
          subtitle: '',
          firstLetter: !!alphabets?.length ? alphabets[0] : '',
          customerName: customer.customerName,
          customerNumber: customer.customerNumber,
          departmentNumber: '0',
          hasDepartments: customer.departments?.length ? true : false,
          isDepartment: false,
          isSelected:
            (selectedCustomerState.find(
              c => c.customerNumber === customer?.customerNumber,
            ) &&
              !customer.departments?.length) ||
            customer.customerNumber === 0,
        };
        customerSelectionOptions.push(customerInfo);
        rowHeights.push(customerRowHeight);
      });
    // By default all customers is selected, if any of the customers is not selected it resets the flag to false
    if (
      customerSelectionOptions.find(c => c.customerNumber === 0) &&
      customerSelectionOptions.find(c => c.isSelected === false)
    ) {
      customerSelectionOptions[
        customerSelectionOptions.findIndex(c => c.customerNumber === 0)
      ].isSelected = false;
    }
    return {
      customerSelectionOptions,
      rowHeights,
      platformType: this.platformService.platformType,
      showAllCustomersOption,
    };
  }

  private createCommonFilters(
    showISRFilter: boolean,
    showOrderNumberFilter: boolean = false,
  ): ModalFilter[] {
    let selectedStatusFilters = [];
    let selectedTransactionFilters = [];
    let fromDate = 0;
    let toDate = 0;
    let selectedCustomers = [];
    let customerPO = '';
    let transactionNumber = '';
    let selectedDocumentation = [];
    let orderNumber = '';

    this.filterSelection.forEach(filter => {
      switch (filter.filterName) {
        case InvoiceFilterIds.CustomerPo:
          customerPO = filter.value as string;
          break;
        case InvoiceFilterIds.TransactionNumber:
          transactionNumber = filter.value as string;
          break;
        case InvoiceFilterIds.FromIssueDate:
          fromDate = filter.value as number;
          break;
        case InvoiceFilterIds.ToIssueDate:
          toDate = filter.value as number;
          break;
        case InvoiceFilterIds.Status:
          selectedStatusFilters = (filter.value as string[]) ?? [];
          break;
        case InvoiceFilterIds.NeedISR:
          selectedDocumentation = (filter.value as string[]) ?? [];
          break;
        case InvoiceFilterIds.TransactionGroup:
          selectedTransactionFilters = (filter.value as string[]) ?? [];
          break;
        case InvoiceFilterIds.Customers:
          selectedCustomers = ((filter.value as number[]) ?? []).map(
            customerNumber =>
              this.createCustomerSelectionOption(customerNumber),
          );
          break;
        case InvoiceFilterIds.OrderNumber:
          orderNumber = filter.value as string;
          break;
      }
    });

    const transactionTypeData: Map<TransactionGroupFilter, string> = new Map([
      [TransactionGroupFilter.INVOICE, 'Invoice'],
      [TransactionGroupFilter.INVOICE_WILL_CALL, 'Will Call Invoice'],
      [TransactionGroupFilter.INVOICE_VENDOR_SHIP, 'Vendor Ship'],
      [TransactionGroupFilter.INVOICE_CHEF_STORE, 'Chef Store'],
      [TransactionGroupFilter.CREDIT, 'Credit'],
      [TransactionGroupFilter.DEBIT, 'Debit'],
    ]);

    const needISRList: Map<string, string> = new Map([
      [InvoiceFilterIds.NeedISR, 'Must include document(s)'],
    ]);

    return [
      {
        filterName: InvoiceFilterIds.TransactionNumber,
        component: UsfFilterTextEntryOptionsComponent,
        data: {
          title: 'Transaction Number',
          subTitle$: new BehaviorSubject(''),
          placeHolderText: 'Up to 12 alpha-numeric characters',
          searchText: transactionNumber,
          validationRegex: new RegExp('^[-0-9a-zA-Z]{1,12}$'),
        },
      },
      {
        filterName: InvoiceFilterIds.CustomerPo,
        component: UsfFilterTextEntryOptionsComponent,
        data: {
          title: 'Customer PO',
          subTitle$: new BehaviorSubject(''),
          placeHolderText: 'Up to 20 alpha numeric characters',
          searchText: customerPO,
          validationRegex: new RegExp('^[-0-9a-zA-Z ]{1,20}$'),
        },
      },
      ...(showOrderNumberFilter
        ? [
            {
              filterName: InvoiceFilterIds.OrderNumber,
              component: UsfFilterTextEntryOptionsComponent,
              data: {
                title: 'Order Number',
                subTitle$: new BehaviorSubject(''),
                placeHolderText: '6 digit number',
                searchText: orderNumber,
                validationRegex: new RegExp('^[0-9]{1,6}$'),
              },
            },
          ]
        : []),
      {
        filterName: InvoiceFilterIds.IssueDate,
        component: UsfFilteringDatesOptionComponent,
        data: {
          title: 'Date Issued',
          subTitle$: new BehaviorSubject(
            this.formattedDateLabel(fromDate, toDate),
          ),
          fromDate: fromDate,
          toDate: toDate,
        },
      },
      {
        filterName: InvoiceFilterIds.Customers,
        component:
          this.platformService.platformType === PlatformEnum.mobile
            ? CustomerSelectionPaymentsFilterMobileComponent
            : CustomerSelectionPaymentsTouchComponent,
        data: {
          title: 'Customer',
          subTitle$: new BehaviorSubject(''),
          vm$: this.getCustomerSelectionVM$(),
          searchText$: this.searchText$,
          show: true,
          isPresentedInFilterModal: false,
          selectedCustomers: selectedCustomers,
        },
      },
      {
        filterName: InvoiceFilterIds.TransactionGroup,
        component: UsfFilterListOptionsComponent,
        data: {
          title: 'Transaction Type',
          subTitle$: new BehaviorSubject(''),
          data: transactionTypeData,
          newSelectedData: selectedTransactionFilters,
        },
      },
      {
        filterName: InvoiceFilterIds.Status,
        component: UsfFilterListOptionsComponent,
        data: {
          title: 'Status',
          subTitle$: new BehaviorSubject(''),
          data: this.filterService.getStatusOptions(),
          newSelectedData: selectedStatusFilters,
        },
      },
      ...(showISRFilter
        ? [
            {
              filterName: InvoiceFilterIds.NeedISR,
              component: UsfFilterListOptionsComponent,
              data: {
                title: 'Documentation',
                subTitle$: new BehaviorSubject(''),
                data: needISRList,
                newSelectedData: selectedDocumentation,
              },
            },
          ]
        : []),
    ];
  }

  private createSorts(showOrderNumberFilter: boolean = false): ModalSort[] {
    return [
      {
        sortName: SortFields.customerName,
        sortDirection: SortDirection.ASC,
        label: 'Customer',
      },
      {
        sortName: SortFields.transaction,
        sortDirection: SortDirection.ASC,
        label: 'Transaction',
      },
      {
        sortName: SortFields.customerPO,
        sortDirection: SortDirection.ASC,
        label: 'Customer PO',
      },
      ...(showOrderNumberFilter
        ? [
            {
              sortName: SortFields.orderNumber,
              sortDirection: SortDirection.ASC,
              label: 'Order Number',
            },
          ]
        : []),
      {
        sortName: SortFields.issueDate,
        sortDirection: SortDirection.DESC,
        label: 'Date Issued',
      },
      {
        sortName: SortFields.amount,
        sortDirection: SortDirection.DESC,
        label: 'Amount',
      },
      {
        sortName: SortFields.amountdue,
        sortDirection: SortDirection.DESC,
        label: 'Amount Due',
      },
      {
        sortName: SortFields.dueDate,
        sortDirection: SortDirection.DESC,
        label: 'Due Date',
      },
      {
        sortName: SortFields.status,
        sortDirection: SortDirection.ASC,
        label: 'Status',
      },
    ];
  }

  private formattedDateFromNumber(date: number) {
    if (!!date) {
      const dateString = date.toString();
      return (
        dateString.substring(4, 6) +
        '/' +
        dateString.substring(6, 8) +
        '/' +
        dateString.substring(0, 4)
      );
    }
    return 'MM/DD/YYYY';
  }

  private formattedDateLabel(fromDate: number, toDate: number) {
    return (
      'From ' +
      this.formattedDateFromNumber(fromDate) +
      ' to ' +
      this.formattedDateFromNumber(toDate)
    );
  }

  private getDateId(
    modalType: ModalType,
    toOrFrom: 'from' | 'to',
  ): AllFilterIds {
    if (toOrFrom === 'from') {
      switch (modalType) {
        case ModalType.arStatement:
          return ArStatementFilterIds.fromStatementDate;
        case ModalType.invoice:
          return InvoiceFilterIds.FromIssueDate;
        case ModalType.paymentActivity:
          return PaymentFilterIds.fromPaymentDate;
      }
    } else {
      switch (modalType) {
        case ModalType.arStatement:
          return ArStatementFilterIds.toStatementDate;
        case ModalType.invoice:
          return InvoiceFilterIds.ToIssueDate;
        case ModalType.paymentActivity:
          return PaymentFilterIds.toPaymentDate;
      }
    }
  }

  private handleModalDismiss(
    event: OverlayEventDetail<PaymentModalOutput>,
    filters: ModalFilter[],
    modalType: ModalType = ModalType.invoice,
  ) {
    if (event?.data) {
      const selectedFilters = [];
      filters.forEach(filter => {
        if (isFilterCalendarData(filter.data)) {
          const calendarData = filter.data as ModalFilterCalendarData;
          if (calendarData.fromDate && calendarData.toDate) {
            selectedFilters.push({
              filterName: this.getDateId(modalType, 'from'),
              value: calendarData.fromDate,
            });
            selectedFilters.push({
              filterName: this.getDateId(modalType, 'to'),
              value: calendarData.toDate,
            });
          }
        } else if (isFilterListData(filter.data)) {
          const listData = filter.data as ModalFilterListData;
          let newData = listData.newSelectedData;
          if (filter.filterName === PaymentFilterIds.paymentMethod) {
            newData = listData.newSelectedData.includes(PaymentMethod.CC)
              ? [...newData, PaymentMethod.AMEX, PaymentMethod.AMX]
              : newData.filter(
                  value =>
                    value !== PaymentMethod.AMEX && value !== PaymentMethod.AMX,
                );
          }
          if (newData.length > 0) {
            selectedFilters.push({
              filterName: filter.filterName,
              value: newData,
            });
          }
        } else if (isFilterCustomerData(filter.data)) {
          const customerData = filter.data as ModalFilterCustomerData;
          if (customerData.selectedCustomers.length > 0) {
            selectedFilters.push({
              filterName: filter.filterName,
              value: customerData.selectedCustomers.map(
                customer => customer.customerNumber,
              ),
            });
          }
        } else if (isFilterTextEntryData(filter.data)) {
          const textEntryData = filter.data as ModalFilterTextData;
          if (textEntryData.searchText.length > 0) {
            selectedFilters.push({
              filterName: filter.filterName,
              value:
                filter.filterName === InvoiceFilterIds.OrderNumber
                  ? Number(textEntryData.searchText)
                  : textEntryData.searchText,
            });
          }
        }
      });

      this.filterService.setFilter(selectedFilters);
      this.filterService.setSort(event.data.sort);
    }
  }

  private createSortsForPaymentActivity(): ModalSort[] {
    return [
      {
        sortName: PaymentActivitySortFields.REFERENCE_NUMBER,
        sortDirection: SortDirection.ASC,
        label: 'Reference Number',
      },
      {
        sortName: PaymentActivitySortFields.PAYMENT_METHOD,
        sortDirection: SortDirection.ASC,
        label: 'Payment Method',
      },
      {
        sortName: PaymentActivitySortFields.PAYMENT_DATE,
        sortDirection: SortDirection.DESC,
        label: 'Payment Date',
      },
      {
        sortName: PaymentActivitySortFields.ITEMS,
        sortDirection: SortDirection.DESC,
        label: 'Number of Items',
      },
      {
        sortName: PaymentActivitySortFields.CUSTOMERS,
        sortDirection: SortDirection.DESC,
        label: 'Number of Customers',
      },
      {
        sortName: PaymentActivitySortFields.PAYMENT_AMOUNT,
        sortDirection: SortDirection.DESC,
        label: 'Payment Amount',
      },
      {
        sortName: PaymentActivitySortFields.STATUS,
        sortDirection: SortDirection.ASC,
        label: 'Status',
      },
    ];
  }

  private createFiltersForPaymentActivity(): ModalFilter[] {
    let selectedStatusFilters = [];
    let fromDate = 0;
    let toDate = 0;
    let selectedCustomers = [];
    let customerPO = '';
    let transactionNumber = '';
    let referenceNumber = '';
    let paymentMethod = [];

    this.filterSelection.forEach(filter => {
      switch (filter.filterName) {
        case PaymentFilterIds.customerPo:
          customerPO = filter.value as string;
          break;
        case PaymentFilterIds.transactionNumber:
          transactionNumber = filter.value as string;
          break;
        case PaymentFilterIds.fromPaymentDate:
          fromDate = filter.value as number;
          break;
        case PaymentFilterIds.toPaymentDate:
          toDate = filter.value as number;
          break;
        case PaymentFilterIds.paymentMethod:
          paymentMethod = (filter.value as string[]) ?? [];
          break;
        case PaymentFilterIds.status:
          selectedStatusFilters = (filter.value as string[]) ?? [];
          break;
        case PaymentFilterIds.referenceNumber:
          referenceNumber = filter.value as string;
          break;
        case PaymentFilterIds.customers:
          selectedCustomers = ((filter.value as number[]) ?? []).map(
            customerNumber =>
              this.createCustomerSelectionOption(customerNumber),
          );
          break;
      }
    });

    const paymentMethodData: Map<PaymentMethod, string> = new Map<
      PaymentMethod,
      string
    >();

    for (let key in PaymentMethod) {
      if (key !== PaymentMethod.AMEX && key !== PaymentMethod.AMX) {
        paymentMethodData.set(
          key as PaymentMethod,
          this.translateService.instant('i18n.payments.paymentMethod.' + key),
        );
      }
    }

    return [
      {
        filterName: PaymentFilterIds.paymentDate,
        component: UsfFilteringDatesOptionComponent,
        data: {
          title: 'Date Range',
          subTitle$: new BehaviorSubject(
            this.formattedDateLabel(fromDate, toDate),
          ),
          fromDate: fromDate,
          toDate: toDate,
          maxDate: dayjs().add(2, 'w').toDate(),
        },
      },
      {
        filterName: PaymentFilterIds.customers,
        component:
          this.platformService.platformType === PlatformEnum.mobile
            ? CustomerSelectionPaymentsFilterMobileComponent
            : CustomerSelectionPaymentsTouchComponent,
        data: {
          title: 'Customer',
          subTitle$: new BehaviorSubject(''),
          vm$: this.getCustomerSelectionVM$(),
          searchText$: this.searchText$,
          show: true,
          isPresentedInFilterModal: false,
          selectedCustomers: selectedCustomers,
        },
      },
      {
        filterName: PaymentFilterIds.status,
        component: UsfFilterListOptionsComponent,
        data: {
          title: 'Payment Status',
          subTitle$: new BehaviorSubject(''),
          data: this.filterService.getStatusOptions(),
          newSelectedData: selectedStatusFilters,
        },
      },
      {
        filterName: PaymentFilterIds.transactionNumber,
        component: UsfFilterTextEntryOptionsComponent,
        data: {
          title: 'Transaction Number',
          subTitle$: new BehaviorSubject(''),
          placeHolderText: 'Up to 12 alpha-numeric characters',
          searchText: transactionNumber,
          validationRegex: new RegExp('^[-0-9a-zA-Z]{1,12}$'),
        },
      },
      {
        filterName: PaymentFilterIds.customerPo,
        component: UsfFilterTextEntryOptionsComponent,
        data: {
          title: 'Customer PO',
          subTitle$: new BehaviorSubject(''),
          placeHolderText: 'Up to 20 alpha numeric characters',
          searchText: customerPO,
          validationRegex: new RegExp('^[-0-9a-zA-Z ]{1,20}$'),
        },
      },
      {
        filterName: PaymentFilterIds.paymentMethod,
        component: UsfFilterListOptionsComponent,
        data: {
          title: 'Payment Method',
          subTitle$: new BehaviorSubject(''),
          data: paymentMethodData,
          newSelectedData: paymentMethod,
        },
      },
      {
        filterName: PaymentFilterIds.referenceNumber,
        component: UsfFilterTextEntryOptionsComponent,
        data: {
          title: 'Reference Number',
          subTitle$: new BehaviorSubject(''),
          placeHolderText: 'Up to 20 alpha numeric characters',
          searchText: referenceNumber,
          validationRegex: new RegExp('^[-0-9a-zA-Z ]{1,20}$'),
        },
      },
    ];
  }

  private createFiltersForPaymentPreferences(): ModalFilter[] {
    let selectedCustomers: CustomerSelectionOption[] = [];
    let isPrimary: string[] = [];

    this.filterSelection.forEach(filter => {
      switch (filter.filterName) {
        case PaymentPreferenceFilterIds.customerName:
          selectedCustomers = ((filter.value as number[]) ?? []).map(customer =>
            this.createCustomerSelectionOption(customer),
          );
          break;
        case PaymentPreferenceFilterIds.type:
          isPrimary = (filter.value as string[]) ?? [];
          break;
      }
    });

    const customerTitle = this.translateService.instant('i18n.common.customer');
    const typeTitle = this.translateService.instant(
      'i18n.payments.paymentPreferences.type',
    );

    const typeData: Map<string, string> = new Map([
      [
        PaymentPreferenceFilterIds.type,
        this.translateService.instant(
          'i18n.payments.paymentPreferences.filters.type.primaryOnly',
        ),
      ],
    ]);

    return [
      {
        filterName: PaymentPreferenceFilterIds.customerName,
        component:
          this.platformService.platformType === PlatformEnum.mobile
            ? CustomerSelectionPaymentsFilterMobileComponent
            : CustomerSelectionPaymentsTouchComponent,
        data: {
          isPresentedInFilterModal: false,
          searchText$: this.searchText$,
          selectedCustomers,
          show: true,
          subTitle$: new BehaviorSubject(''),
          title: customerTitle,
          vm$: this.getCustomerSelectionVM$(),
        },
      },
      {
        filterName: PaymentPreferenceFilterIds.type,
        component: UsfFilterListOptionsComponent,
        data: {
          canSelectMultiple: false,
          data: typeData,
          newSelectedData: isPrimary,
          subTitle$: new BehaviorSubject(''),
          title: typeTitle,
        },
      },
    ];
  }

  private createSortsForPaymentPreferences(): ModalSort[] {
    return [
      {
        label: this.translateService.instant(
          'i18n.payments.paymentPreferences.sorts.customer',
        ),
        sortDirection: SortDirection.ASC,
        sortName: PaymentPreferenceSortFields.customerName,
      },
      {
        label: this.translateService.instant(
          'i18n.payments.paymentPreferences.sorts.nickname',
        ),
        sortDirection: SortDirection.ASC,
        sortName: PaymentPreferenceSortFields.nickname,
      },

      {
        label: this.translateService.instant(
          'i18n.payments.paymentPreferences.sorts.bank',
        ),
        sortDirection: SortDirection.ASC,
        sortName: PaymentPreferenceSortFields.bank,
      },

      {
        label: this.translateService.instant(
          'i18n.payments.paymentPreferences.sorts.accountNumber',
        ),
        sortDirection: SortDirection.ASC,
        sortName: PaymentPreferenceSortFields.accountNumber,
      },

      {
        label: this.translateService.instant(
          'i18n.payments.paymentPreferences.sorts.routingNumber',
        ),
        sortDirection: SortDirection.ASC,
        sortName: PaymentPreferenceSortFields.routingNumber,
      },

      {
        label: this.translateService.instant(
          'i18n.payments.paymentPreferences.sorts.type',
        ),
        sortDirection: SortDirection.DESC,
        sortName: PaymentPreferenceSortFields.type,
      },
    ];
  }

  private createFiltersForDashboard(
    hideUsersCustomers: boolean,
    additionalCustomers: Customer[],
  ): ModalFilter[] {
    let selectedCustomers: CustomerSelectionOption[] = [];
    let creditStatus: string[] = [];
    let lastPaymentMethod: string[] = [];

    this.filterSelection.forEach(filter => {
      switch (filter.filterName) {
        case DashboardFilterIds.creditStatus:
          creditStatus = (filter.value as string[]) ?? [];
          break;
        case DashboardFilterIds.customer:
          selectedCustomers = ((filter.value as number[]) ?? []).map(
            customerNumber =>
              this.createCustomerSelectionOption(customerNumber),
          );
          break;
        case DashboardFilterIds.lastPaymentMethod:
          lastPaymentMethod = (filter.value as string[]) ?? [];
          break;
      }
    });

    const customerTitle = this.translateService.instant(
      'i18n.payments.paymentDashboard.filters.customer',
    );
    const creditStatusTitle = this.translateService.instant(
      'i18n.payments.paymentDashboard.filters.creditStatus',
    );
    const lastPaymentMethodTitle = this.translateService.instant(
      'i18n.payments.paymentDashboard.filters.lastPaymentMethod',
    );

    const creditStatusData = new Map<string, string>();
    for (let key in CreditStatus) {
      creditStatusData.set(
        CreditStatus[key],
        this.translateService.instant(
          'i18n.payments.paymentDashboard.creditStatus.' + key,
        ),
      );
    }
    const lastPaymentMethodData = new Map<string, string>();
    for (let key in LastPaymentMethod) {
      lastPaymentMethodData.set(
        LastPaymentMethod[key],
        this.translateService.instant(
          'i18n.payments.paymentDashboard.lastPaymentMethod.' + key,
        ),
      );
    }

    return [
      {
        filterName: DashboardFilterIds.customer,
        component:
          this.platformService.platformType === PlatformEnum.mobile
            ? CustomerSelectionPaymentsFilterMobileComponent
            : CustomerSelectionPaymentsTouchComponent,
        data: {
          isPresentedInFilterModal: false,
          searchText$: this.searchText$,
          selectedCustomers: selectedCustomers,
          show: true,
          subTitle$: new BehaviorSubject(''),
          title: customerTitle,
          vm$: this.getCustomerSelectionVM$(
            undefined,
            undefined,
            hideUsersCustomers,
            additionalCustomers,
          ),
        },
      },
      {
        filterName: DashboardFilterIds.creditStatus,
        component: UsfFilterListOptionsComponent,
        data: {
          title: creditStatusTitle,
          subTitle$: new BehaviorSubject(''),
          data: creditStatusData,
          newSelectedData: creditStatus,
        },
      },
      {
        filterName: DashboardFilterIds.lastPaymentMethod,
        component: UsfFilterListOptionsComponent,
        data: {
          title: lastPaymentMethodTitle,
          subTitle$: new BehaviorSubject(''),
          canSelectMultiple: true,
          data: lastPaymentMethodData,
          newSelectedData: lastPaymentMethod,
        },
      },
    ];
  }

  private createSortsForDashboard(): ModalSort[] {
    return [
      {
        label: this.translateService.instant(
          'i18n.payments.paymentDashboard.filters.customer',
        ),
        sortDirection: SortDirection.ASC,
        sortName: DashboardSortFields.customer,
      },
      {
        label: this.translateService.instant(
          'i18n.payments.paymentDashboard.filters.creditStatus',
        ),
        sortDirection: SortDirection.ASC,
        sortName: DashboardSortFields.creditStatus,
      },
      {
        label: this.translateService.instant(
          'i18n.payments.paymentDashboard.filters.openInvoices',
        ),
        sortDirection: SortDirection.DESC,
        sortName: DashboardSortFields.openInvoices,
      },
      {
        label: this.translateService.instant(
          'i18n.payments.paymentDashboard.filters.unusedCredits',
        ),
        sortDirection: SortDirection.DESC,
        sortName: DashboardSortFields.unusedCredits,
      },
      {
        label: this.translateService.instant(
          'i18n.payments.paymentDashboard.filters.lastPaymentMethod',
        ),
        sortDirection: SortDirection.ASC,
        sortName: DashboardSortFields.paymentMethod,
      },
      {
        label: this.translateService.instant(
          'i18n.payments.paymentDashboard.filters.percentageCurrent',
        ),
        sortDirection: SortDirection.DESC,
        sortName: DashboardSortFields.percentageCurrent,
      },
      {
        label: this.translateService.instant(
          'i18n.payments.paymentDashboard.filters.accountBalance',
        ),
        sortDirection: SortDirection.DESC,
        sortName: DashboardSortFields.accountBalance,
      },
      {
        label: this.translateService.instant(
          'i18n.payments.paymentDashboard.filters.currentBalance',
        ),
        sortDirection: SortDirection.DESC,
        sortName: DashboardSortFields.currentBalance,
      },
      {
        label: this.translateService.instant(
          'i18n.payments.paymentDashboard.filters.overdueBalance',
        ),
        sortDirection: SortDirection.DESC,
        sortName: DashboardSortFields.overdueBalance,
      },
      {
        label: this.translateService.instant(
          'i18n.payments.paymentDashboard.filters.term',
        ),
        sortDirection: SortDirection.ASC,
        sortName: DashboardSortFields.term,
      },
      {
        label: this.translateService.instant(
          'i18n.payments.paymentDashboard.filters.oneSevenDays',
        ),
        sortDirection: SortDirection.DESC,
        sortName: DashboardSortFields.oneSevenDays,
      },
      {
        label: this.translateService.instant(
          'i18n.payments.paymentDashboard.filters.eightFifteenDays',
        ),
        sortDirection: SortDirection.DESC,
        sortName: DashboardSortFields.eightFifteenDays,
      },
      {
        label: this.translateService.instant(
          'i18n.payments.paymentDashboard.filters.sixteenThirtyDays',
        ),
        sortDirection: SortDirection.DESC,
        sortName: DashboardSortFields.sixteenThirtyDays,
      },
      {
        label: this.translateService.instant(
          'i18n.payments.paymentDashboard.filters.thirtyPlusDPT',
        ),
        sortDirection: SortDirection.DESC,
        sortName: DashboardSortFields.thirtyPlus,
      },
    ];
  }

  private createFiltersForArStatements(): ModalFilter[] {
    let customers = [];
    let fromStatementDate = 0;
    let toStatementDate = 0;

    this.filterSelection.forEach(filter => {
      switch (filter.filterName) {
        case ArStatementFilterIds.customers:
          customers = ((filter.value as number[]) ?? []).map(customerNumber =>
            this.createCustomerSelectionOption(customerNumber),
          );
          break;
        case ArStatementFilterIds.fromStatementDate:
          fromStatementDate = filter.value as number;
          break;
        case ArStatementFilterIds.toStatementDate:
          toStatementDate = filter.value as number;
          break;
      }
    });

    return [
      {
        filterName: ArStatementFilterIds.customers,
        component:
          this.platformService.platformType === PlatformEnum.mobile
            ? CustomerSelectionPaymentsFilterMobileComponent
            : CustomerSelectionPaymentsTouchComponent,
        data: {
          title: 'Customer',
          subTitle$: new BehaviorSubject(''),
          vm$: this.getCustomerSelectionVM$(true),
          searchText$: this.searchText$,
          show: true,
          isPresentedInFilterModal: false,
          selectedCustomers: customers,
        },
      },
      {
        filterName: ArStatementFilterIds.statementDate,
        component: UsfFilteringDatesOptionComponent,
        data: {
          title: 'Statement Date',
          subTitle$: new BehaviorSubject(
            this.formattedDateLabel(fromStatementDate, toStatementDate),
          ),
          fromDate: fromStatementDate,
          toDate: toStatementDate,
          message: this.translateService.instant(
            'i18n.payments.arStatements.headsUpStatementDate',
          ),
          minDate: new Date('2/3/25'),
        },
      },
    ];
  }

  private createSortForArStatements(): ModalSort[] {
    return [
      {
        label: 'Customer',
        sortDirection: SortDirection.ASC,
        sortName: ArStatementSortFields.customerName,
      },
      {
        label: 'Statement Date',
        sortDirection: SortDirection.DESC,
        sortName: ArStatementSortFields.statementDate,
      },
    ];
  }

  private createCustomerSelectionOption(
    customerNumber: number,
  ): CustomerSelectionOption {
    return {
      customerNumber,
      title: '',
      departmentNumber: '0',
      isSelected: true,
    };
  }
}
